import { Box } from '@factorialco/deprecated-design-system'
import { TwoColumnLayout } from '@factorialco/f0-react'
import { OneAlert } from '@factorialco/f0-react/dist/experimental'
import { CardPin } from '@factorialco/f0-react/icons/app'
import {
  ProcurementApprovalTimelineEntry,
  PurchaseRequestFragment,
} from 'generated/resources/graphql'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import TimelineItem from 'modules/procurement/components/TimelineItem'
import PurchaseRequestSummarySidebar from 'modules/procurement/pages/PurchaseRequestDetailPage/PurchaseRequestSummarySidebar'

interface Props {
  purchaseRequest: PurchaseRequestFragment
  timelineEntries: ProcurementApprovalTimelineEntry[]
}

const WorkflowApprovalLayout = ({ purchaseRequest, timelineEntries }: Props) => {
  const approvalProcess = purchaseRequest?.approvalProcess
  const reviewRequests = approvalProcess?.reviewRequests || []

  const formatAlertDate = (timestamp: string) => {
    const date = stringToDate(timestamp, 'backend_datetime')
    return i18nDateToString(date, 'ui_short_with_time')
  }

  const isAutoRejected =
    approvalProcess?.decision === 'rejected' &&
    !approvalProcess?.decisionReason &&
    !reviewRequests.some((reviewRequest) => reviewRequest.decision === 'rejected')

  const automaticRejectionAlert =
    isAutoRejected && approvalProcess?.updatedAt ? (
      <OneAlert
        variant="critical"
        title={i18n.t(
          'procurement.purchase_requests.detail.timeline.automatic_rejection_alert.title'
        )}
        description={i18n.t(
          'procurement.purchase_requests.detail.timeline.automatic_rejection_alert.description',
          {
            date: formatAlertDate(approvalProcess.updatedAt),
          }
        )}
        icon={CardPin}
      />
    ) : null

  const getAlertForEntry = (entry: ProcurementApprovalTimelineEntry) => {
    const {
      actionGroup,
      status,
      decisionReason,
      time: timestamp,
      assigneeNames,
      employeeSegmentName,
    } = entry

    if (actionGroup !== 'approvals' || status !== 'rejected' || !decisionReason) {
      return undefined
    }

    const matchingReviewRequest = reviewRequests.find(
      (reviewRequest) =>
        reviewRequest.decisionReason === decisionReason && reviewRequest.decision === 'rejected'
    )

    const reviewerTime = matchingReviewRequest?.reviewedAt || timestamp
    const reviewerName = assigneeNames?.[0] || employeeSegmentName || ''

    return (
      <OneAlert
        variant="critical"
        title={i18n.t('procurement.purchase_requests.detail.timeline.rejected_alert.title', {
          reason: decisionReason,
        })}
        description={`${reviewerName}, ${formatAlertDate(reviewerTime)}`}
        icon={CardPin}
      />
    )
  }

  const mainContent = (
    <Box gap="s24">
      {automaticRejectionAlert}
      <Box gap="s8">
        {timelineEntries.map((entry, index) => (
          <TimelineItem
            key={index}
            entry={entry}
            hasConnectingLine={index !== timelineEntries.length - 1}
            alert={getAlertForEntry(entry)}
          />
        ))}
      </Box>
    </Box>
  )

  const sideContent = <PurchaseRequestSummarySidebar purchaseRequest={purchaseRequest} />

  return <TwoColumnLayout sideContent={sideContent}>{mainContent}</TwoColumnLayout>
}

export default WorkflowApprovalLayout
